import React, {useEffect, useRef, useState} from "react";
import {useOutsiderDetector} from "../../../Hooks/useOutsiderDetector";
import {GetObjectIndexByKeyValue} from "../../../Constants/Helpers";
import useComboBox from "./useComboBox";
import useFilterSelectOptions from "../../../Hooks/useFilterSelectOptions";
import {ComboBoxControlType} from "./ComboBoxTypes";
import ComboBoxListActions from "./Components/ComboBoxListActions";
import ComboBoxFilter from "./Components/ComboBoxFilter";
import ComboBoxItemToggle from "./Components/ComboBoxItemToggle";
import {useController, useFormContext} from "react-hook-form";
import {ArraysAreEqual} from "../../../Constants/DataHelpers";
import useKeyboardNavigation from "../../../Hooks/useKeyboardNavigation";

const ComboBoxControl = (props: ComboBoxControlType) => {
    const { control, watch } = useFormContext();

    const [ preSelectedValues, setPreSelectedValues ] = useState<any>(props.defaultValues ?? []);

    const {
        field,
        fieldState,
    } = useController({
        control,
        name: props.name,
        defaultValue: props.defaultValues,
        rules: {
            required: props.required ? `${props.label} is required` : false,
            validate: (e) => props.required ? e.length > 0 ? true : `${props.label} is required` : true
        },
    });

    const [ isMinimal ] = useState<boolean>(props.minimal ?? false);
    const [ listAlwaysVisible ] = useState<boolean>(props.listAlwaysVisible ?? false);
    
    const [ active, setActive ] = useState<boolean>(listAlwaysVisible ?? false);
    const [ displayValue, setDisplayValue ] = useState<string>('');

    const multiSelectRef = useRef<HTMLDivElement>(null);
    const filterRef = useRef<HTMLInputElement>(null);

    const FilterStartCallback = () => {
        if(!active) {
            setActive(true)
        }
    }

    const {
        allSelected,
        selectedIds,
        selectedIdsRef,
        ToggleSingle,
        ToggleAll,
        ResetSelection
    } = useComboBox(props.data, 'value', 'multiSelect', props.valueType, preSelectedValues);

    const {
        filterSelectValue,
        filterSelectItems,
        FilterSelectList,
        ResetFilterSelectOptions,
        HandleFilterSelectBlur,
        HandleFilterSelectFocus
    } = useFilterSelectOptions({ filterData: props.data, onStartCallback: FilterStartCallback });


    useOutsiderDetector(multiSelectRef, () => active ? CloseOptions() : null);

    const ForwardChanges = (data: any) => {
        field.onChange(data);
    }

    const CloseOptions = () => {
        UpdateDisplayValue();
        setActive(listAlwaysVisible ?? false);
    }

    const HandleSelection = (e: any, item: any) => {
        const entry = item ? item : {
            label: e?.target.getAttribute('title'),
            value: parseInt(e?.target.getAttribute('data-value'))
        };

        if(e.code && e.code === 'ArrowDown' || e.code === 'ArrowUp') {
            HandleKeyboardElementNavigation(e);
        } else if(!e.code) {
            ForwardChanges(ToggleSingle(entry.value, e.target.checked));
        }
    }

    const UpdateDisplayValue = () => {
        let formatDisplayValue = selectedIdsRef.current.length > 0 ? selectedIdsRef.current.length === 1 ? `${selectedIdsRef.current.length} Item Selected` : `${selectedIdsRef.current.length} Items Selected` : '';

        if(selectedIdsRef.current.length === 1) {
            const value = GetObjectIndexByKeyValue(props.data, 'value', selectedIdsRef.current[0]);

            if(value !== -1) {
                formatDisplayValue = props.data[value].label;
            }
        }

        setDisplayValue(selectedIdsRef.current.length > 0 ? active ? filterSelectValue : formatDisplayValue : '');
    }

    const HandleFilterSelectBlurCallback = (e: any) => {
        if(active) {
            setActive(listAlwaysVisible ?? false);
        }
    }

    const HandleFilterSelectFocusCallback = () => {
        setActive(true);
    }

    const {
        HandleElementTransitionNavigation,
        HandleKeyboardElementNavigation,
        optionsCycled
    } = useKeyboardNavigation({
        itemClassName: 'checkbox',
        itemContainerClassSelector: '.multi-select-active',
        skipClassName: 'multi-select-actions',
        resetCallback: ResetFilterSelectOptions,
    });

    useEffect(() => {
        if(optionsCycled) {
            CloseOptions();
        }
    }, [optionsCycled]);

    useEffect(() => {
        UpdateDisplayValue();
    }, [active, selectedIds]);
    
    useEffect(() => {
        if(filterRef.current) {
            filterRef.current.value = displayValue
        }
    }, [displayValue]);

    useEffect(() => {
        if(props.defaultValues) {
            setPreSelectedValues(props.defaultValues);
        } else {
            ResetSelection();
        }
        ResetFilterSelectOptions();
    }, [props.reset]);

    useEffect(() => {
        const subscription = watch((e) => {
            if(e[props.name] && !ArraysAreEqual(e[props.name], selectedIds)) {
                setPreSelectedValues(e[props.name]);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    return (
        <div className={'flex-column multi-select-container flex-gap-lg'} ref={multiSelectRef}>
            <div className={listAlwaysVisible && !isMinimal ? 'multi-select-inline-control' : ''}>

                <ComboBoxFilter
                    filterRef={filterRef}
                    filterSelectValue={displayValue}
                    placeholder={props.placeholder}
                    label={props.label ?? 'ComboBox'}
                    resetCallback={(e: any) => ResetFilterSelectOptions()}
                    blurCallback={(e: any) => HandleFilterSelectBlur(e, HandleFilterSelectBlurCallback)}
                    filterActionCallback={(e: any) => FilterSelectList(e)}
                    filterNavigationCallback={(e: any) => HandleElementTransitionNavigation(e)}
                    filterFocusCallback={(e: any) => HandleFilterSelectFocus(e, HandleFilterSelectFocusCallback)}
                    minimal={isMinimal}
                    errorMessage={fieldState.error?.message as string}
                    isValid={!fieldState.error ? true : null}
                    isActive={active}
                    isLoading={props.isLoading}
                />

                <div className={`${listAlwaysVisible && !isMinimal ? 'multi-select-inline-list' : 'generic-menu-container'} ${isMinimal ? 'in-dropdown-menu' : ''} multi-select-entries ${active ? 'multi-select-active' : ''}`} style={{ maxHeight: '300px', overflow: 'auto'}} tabIndex={-1}>
                    {filterSelectValue === '' && (
                        <ComboBoxListActions allSelected={allSelected} toggleCallBack={(e: boolean) => ForwardChanges(ToggleAll(e))}/>
                    )}

                    {filterSelectItems && filterSelectItems.length > 0 &&  filterSelectItems.map((item, index: number) => {
                        if(item.value === undefined || item.value === '') return null;

                        return (
                            <ComboBoxItemToggle
                                key={`comboItem${props.label}${item.value}${index}`}
                                name={`${props.name}${index}`}
                                label={props.label}
                                item={item}
                                selectedValues={selectedIds}
                                indeterminateValues={props.indeterminateValues}
                                onChangeCallback={(e: any) => HandleSelection(e, item)}
                                onKeyDownCallback={(e: any) => HandleKeyboardElementNavigation(e)}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    )
};

export default ComboBoxControl
